import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDistanceToNow } from "date-fns";
import { apiClient } from "../../services/apiClient";
import clsx from "clsx";

const DocumentSidebar = ({
  isOpen,
  onClose,
  proposalId,
  onReferenceDocument,
  referencedDocuments = [],
  onUpload,
  refreshTrigger,
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  // Debug logging for mount/unmount
  // useEffect(() => {
  //   console.log("DocumentSidebar mounted/updated:", {
  //     isOpen,
  //     proposalId,
  //     documentsCount: documents.length,
  //   });

  //   return () => {
  //     console.log("DocumentSidebar cleanup/unmount");
  //   };
  // }, [isOpen, proposalId, documents.length]);

  useEffect(() => {
    if (proposalId && isOpen) {
      fetchDocuments();
    }
  }, [proposalId, isOpen, refreshTrigger]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getProposalDocuments(proposalId);

      if (response.success) {
        // Sort documents by priority (lower priority number = higher importance)
        const sortedDocuments = (response.data.documents || []).sort((a, b) => {
          // First by document type priority
          const aPriority = getDocumentPriority(a.type_name);
          const bPriority = getDocumentPriority(b.type_name);

          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          // Then by file size (larger files first within same priority)
          return (b.file_size || 0) - (a.file_size || 0);
        });

        setDocuments(sortedDocuments);
      } else {
        throw new Error(response.message || "Failed to fetch documents");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setError(error.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentPriority = (typeName) => {
    const priorities = {
      Solicitation: 1,
      RFP: 1,
      Scope_of_Work: 2,
      SOW: 2,
      Evaluation: 3,
      Technical: 4,
      Contract: 5,
      Bid_Sheet: 6,
      Past_Performance: 7,
      Resume: 8,
      Proposal: 9,
      Supporting: 10,
    };

    for (const [type, priority] of Object.entries(priorities)) {
      if (typeName && typeName.includes(type)) {
        return priority;
      }
    }
    return 10;
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "file-pdf", color: "text-red-500" };
      case "doc":
      case "docx":
        return { icon: "file-word", color: "text-blue-500" };
      case "xls":
      case "xlsx":
        return { icon: "file-excel", color: "text-emerald-500" };
      case "ppt":
      case "pptx":
        return { icon: "file-powerpoint", color: "text-orange-500" };
      default:
        return { icon: "file-alt", color: "text-slate-500" };
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: { bg: "bg-amber-100", text: "text-amber-700", icon: "clock" },
      Processing: { bg: "bg-blue-100", text: "text-blue-700", icon: "spinner" },
      Completed: {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        icon: "check",
      },
      Failed: { bg: "bg-red-100", text: "text-red-700", icon: "times" },
    };

    const config = statusConfig[status] || statusConfig["Pending"];

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <FontAwesomeIcon
          icon={config.icon}
          className={`mr-1 ${status === "Processing" ? "animate-spin" : ""}`}
        />
        {status}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const getPriorityColor = (typeName) => {
    const priority = getDocumentPriority(typeName);
    if (priority <= 2) return "border-l-4 border-l-red-500";
    if (priority <= 4) return "border-l-4 border-l-orange-500";
    if (priority <= 6) return "border-l-4 border-l-blue-500";
    return "border-l-4 border-l-slate-300";
  };

  const filteredDocuments = documents.filter((doc) => {
    if (filter === "all") return true;
    if (filter === "important") return getDocumentPriority(doc.type_name) <= 4;
    if (filter === "completed") return doc.processing_status === "Completed";
    return doc.type_name?.toLowerCase().includes(filter.toLowerCase());
  });

  const sidebarClasses = clsx(
    "w-80 bg-slate-50 border-l border-slate-200 flex flex-col transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
    {
      "translate-x-0": isOpen,
      "translate-x-full": !isOpen,
      "fixed inset-y-0 right-0 z-50 lg:static": true,
    }
  );

  const handleClose = () => {
    console.log(
      "DocumentSidebar handleClose called for proposalId:",
      proposalId
    );
    if (proposalId) {
      console.log("Ignoring close request for proposal chat");
      return; // Don't close for proposal chats
    }
    onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={handleClose}
        />
      )}

      {/* Sidebar */}
      <div className={sidebarClasses}>
        {/* Mobile close button - only show on mobile and NOT for proposal chats */}
        {!proposalId && (
          <div className="lg:hidden flex items-center justify-start h-16 px-6 border-b border-slate-200 flex-shrink-0 bg-white">
            <button
              onClick={handleClose}
              className="p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100"
            >
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        )}

        {/* Header - Updated with Upload Button */}
        <div className="p-4 border-b border-slate-200 bg-white flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 flex items-center">
              <FontAwesomeIcon
                icon="file-alt"
                className="mr-2 text-primary-500"
              />
              Documents
            </h2>

            <div className="flex items-center space-x-2">
              {/* Upload Button - moved from main header */}
              {onUpload && (
                <button
                  onClick={onUpload}
                  className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors flex items-center"
                  title="Upload Documents"
                >
                  <FontAwesomeIcon icon="upload" className="text-xs" />
                </button>
              )}

              {/* Only show close button for non-proposal chats */}
              {!proposalId && (
                <button
                  onClick={handleClose}
                  className="hidden lg:block p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                >
                  <FontAwesomeIcon icon="times" className="text-slate-500" />
                </button>
              )}
            </div>
          </div>

          {/* Filter dropdown */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Documents</option>
            <option value="important">Important (High Priority)</option>
            <option value="completed">Processing Completed</option>
            <option value="RFP">RFP Documents</option>
            <option value="Proposal">Proposal Documents</option>
            <option value="Supporting">Supporting Documents</option>
          </select>
        </div>

        {/* Documents list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon
                icon="spinner"
                className="animate-spin text-slate-400 text-xl"
              />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon="exclamation-triangle"
                className="text-red-500 text-xl mb-2"
              />
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={fetchDocuments}
                className="mt-2 text-xs text-primary-600 hover:text-primary-800"
              >
                Try again
              </button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon="file-alt"
                className="text-slate-300 text-xl mb-2"
              />
              <p className="text-sm text-slate-600">
                {filter === "all"
                  ? "No documents found"
                  : "No matching documents"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map((document) => {
                const fileIcon = getFileIcon(document.original_file_name);
                const priorityColor = getPriorityColor(document.type_name);
                const isReferenced = referencedDocuments.some(
                  (ref) => ref.document_id === document.document_id
                );

                // Add isReferenced property to document for button styling
                const documentWithRef = { ...document, isReferenced };

                return (
                  <div
                    key={document.document_id}
                    className={`bg-white border rounded-md p-3 hover:shadow-md transition-all duration-200 ${priorityColor} ${
                      isReferenced
                        ? "border-blue-300 shadow-sm ring-1 ring-blue-200"
                        : "border-slate-200"
                    }`}
                  >
                    {/* Document title - UPDATED for wrapping */}
                    <div className="flex items-start space-x-2 mb-2">
                      <div className="flex-shrink-0 mt-1">
                        <FontAwesomeIcon
                          icon={fileIcon.icon}
                          className={`text-sm ${fileIcon.color}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 text-sm leading-tight break-words">
                          {document.original_file_name.replace(/_/g, " ")}
                          {isReferenced && (
                            <FontAwesomeIcon
                              icon="link"
                              className="ml-2 text-blue-500 text-xs"
                            />
                          )}
                        </h4>
                      </div>
                    </div>

                    {/* Document info and action buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col space-y-1 text-xs text-slate-500 flex-1 min-w-0">
                        <span className="text-slate-600 font-medium">
                          {document.type_name || "Unknown Type"}
                        </span>
                        {document.page_count && (
                          <span>{document.page_count} pages</span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() =>
                            apiClient.viewDocument(document.document_id)
                          }
                          disabled={document.processing_status !== "Completed"}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View document"
                        >
                          <FontAwesomeIcon icon="eye" className="text-xs" />
                        </button>
                        <button
                          onClick={() => onReferenceDocument?.(documentWithRef)}
                          disabled={document.processing_status !== "Completed"}
                          className={`p-1 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded ${
                            isReferenced
                              ? "text-blue-600 bg-blue-100 hover:bg-blue-200"
                              : "text-slate-400 hover:text-primary-600 hover:bg-primary-50"
                          }`}
                          title={
                            isReferenced
                              ? "Remove from chat"
                              : "Reference in chat"
                          }
                        >
                          <FontAwesomeIcon
                            icon={isReferenced ? "unlink" : "plus"}
                            className="text-xs"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="text-xs text-slate-500 text-center">
            {filteredDocuments.length} of {documents.length} documents
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentSidebar;
